import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LuCheck } from 'react-icons/lu'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'

import Modal from './Modal'
import { callTool } from '../lib/tools'
import useOrganizations from '../models/useOrganizations'

type Status = 'idle' | 'accepting' | 'accepted' | 'error'

// Invite accept landing — sits under AuthGuard, so a logged-out invitee is sent
// to /login and returned here (token preserved) after AuthKit sign-in. Once
// authed, accepting calls org_invite_accept, makes the joined org active, and
// redirects to the members page. Mirrors the access-request landing.
export default function InviteAccept() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [status, setStatus] = useState<Status>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleAccept = async () => {
    if (!token) return
    setStatus('accepting')
    setErrorMessage('')
    try {
      const res = await callTool<{ org_id: string; role: string }>('org_invite_accept', { token })
      // Load the new membership, then switch into the joined org.
      await useOrganizations.getState().get()
      if (res.org_id) useOrganizations.getState().setActiveOrg(res.org_id)
      setStatus('accepted')
      navigate('/org/members')
    } catch (e) {
      setStatus('error')
      setErrorMessage(e instanceof Error ? e.message : 'This invitation is no longer valid.')
    }
  }

  return (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: 1,
        minHeight: '95vh'
      }}>
      <Modal open inline maxWidth="xs" color="secondary.main">
        <DialogContent>
          <Stack
            sx={{
              gap: 3,
              py: 1
            }}>
            <Typography variant="subtitle1" color="secondary">
              Organization invitation
            </Typography>

            {!token ? (
              <Alert severity="error">This invitation link is invalid.</Alert>
            ) : status === 'error' ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : status === 'accepted' ? (
              <Alert severity="success" icon={<LuCheck />}>
                Invitation accepted. Taking you to the organization…
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{
                  color: 'text.secondary'
                }}>
                  You&apos;ve been invited to join an organization on noBGP.
                </Typography>
                <Button
                  size="large"
                  variant="contained"
                  color="secondary"
                  onClick={handleAccept}
                  disabled={status === 'accepting'}
                >
                  {status === 'accepting' ? (
                    <>
                      Accepting…
                      <CircularProgress color="inherit" size={18} />
                    </>
                  ) : (
                    'Accept invitation'
                  )}
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
      </Modal>
    </Stack>
  )
}
